import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  FileSpreadsheet,
  Play,
  Rows3,
  SlidersHorizontal,
} from 'lucide-react';
import { useState } from 'react';

import { Button } from '../../components/Button';
import {
  FIELD_DEFINITIONS,
  REQUIRED_FIELD_KEYS,
  getFieldDefinition,
  type FieldKey,
} from '../../domain';
import type { ParsedWorkbookMeta, ParsedSheetMeta } from '../inspection/parser';
import { columnName, mappingNeedsAttention, type SheetMappingState } from './model';

export interface MappingScreenProps {
  readonly meta: ParsedWorkbookMeta;
  readonly states: readonly SheetMappingState[];
  readonly error: string | null;
  readonly validationMessages: readonly string[];
  readonly onChange: (index: number, state: SheetMappingState) => void;
  readonly onHeaderRowChange: (index: number, sheet: ParsedSheetMeta, row: number) => void;
  readonly onFieldChange: (index: number, field: FieldKey, column: number | null) => void;
  readonly onBack: () => void;
  readonly onStart: () => void;
}

export function MappingScreen({
  meta,
  states,
  error,
  validationMessages,
  onChange,
  onHeaderRowChange,
  onFieldChange,
  onBack,
  onStart,
}: MappingScreenProps) {
  const selectedCount = states.filter(({ selected }) => selected).length;
  return (
    <main className="min-h-[calc(100dvh-7rem)] bg-slate-100">
      <div className="mx-auto max-w-[1440px] px-4 py-6 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 border-b border-slate-300 pb-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <button
              type="button"
              onClick={onBack}
              className="mb-3 inline-flex items-center gap-2 rounded-md text-sm font-semibold text-slate-600 outline-none hover:text-slate-900 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-orange-600"
            >
              <ArrowLeft aria-hidden="true" className="size-4" />
              更换文件
            </button>
            <h1 className="text-2xl font-black tracking-tight text-slate-900 sm:text-3xl">
              确认工作表与字段映射
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
              自动识别只用于减少操作。请确认表头行和每个字段对应的列，再开始检查。
            </p>
          </div>
          <div className="flex min-w-0 items-center gap-3 rounded-lg border border-slate-300 bg-white px-4 py-3 shadow-sm">
            <FileSpreadsheet aria-hidden="true" className="size-5 shrink-0 text-slate-700" />
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-slate-900" title={meta.fileName}>
                {meta.fileName}
              </p>
              <p className="mt-0.5 text-xs text-slate-500">
                {formatFileSize(meta.fileSize)}，{meta.sheets.length} 个工作表
              </p>
            </div>
          </div>
        </div>

        {error !== null && (
          <div
            role="alert"
            className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm leading-6 text-red-800"
          >
            {error}
          </div>
        )}

        <section className="mt-6 rounded-xl border border-slate-300 bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <div>
              <h2 className="font-bold text-slate-900">工作表</h2>
              <p className="mt-1 text-xs text-slate-500">
                已选择 {selectedCount} 个，规则按工作表分别运行。
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {meta.sheets.map((sheet, index) => (
                <label
                  key={sheet.name}
                  className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition ${
                    states[index]?.selected
                      ? 'border-slate-700 bg-slate-800 text-white'
                      : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="size-4 accent-orange-600"
                    checked={states[index]?.selected ?? false}
                    onChange={(event) => {
                      const current = states[index];
                      if (current !== undefined)
                        onChange(index, { ...current, selected: event.target.checked });
                    }}
                  />
                  {sheet.name}
                  <span className={states[index]?.selected ? 'text-slate-300' : 'text-slate-400'}>
                    {sheet.effectiveRowCount} 行
                  </span>
                </label>
              ))}
            </div>
          </div>
        </section>

        <div className="mt-5 space-y-5">
          {meta.sheets.map((sheet, index) => {
            const state = states[index];
            if (state === undefined || !state.selected) return null;
            return (
              <SheetMappingEditor
                key={sheet.name}
                sheet={sheet}
                state={state}
                onChange={(next) => onChange(index, next)}
                onHeaderRowChange={(row) => onHeaderRowChange(index, sheet, row)}
                onFieldChange={(field, column) => onFieldChange(index, field, column)}
              />
            );
          })}
        </div>

        {selectedCount === 0 && (
          <div className="mt-5 rounded-xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
            <Rows3 aria-hidden="true" className="mx-auto size-8 text-slate-400" />
            <p className="mt-3 font-bold text-slate-800">请至少选择一个工作表</p>
          </div>
        )}

        <div className="sticky bottom-0 z-10 mt-6 border-t border-slate-300 bg-slate-100 py-4">
          <div className="flex flex-col gap-3 rounded-xl border border-slate-300 bg-white p-4 shadow-[0_-8px_30px_rgba(31,52,72,0.08)] sm:flex-row sm:items-center sm:justify-between">
            <div aria-live="polite" className="min-w-0 text-sm">
              {validationMessages.length === 0 ? (
                <p className="inline-flex items-center gap-2 font-semibold text-emerald-800">
                  <CheckCircle2 aria-hidden="true" className="size-5" />
                  映射已确认，可以开始检查
                </p>
              ) : (
                <div className="flex items-start gap-2 text-amber-900">
                  <AlertTriangle aria-hidden="true" className="mt-0.5 size-5 shrink-0" />
                  <ul className="space-y-1">
                    {validationMessages.slice(0, 3).map((message) => (
                      <li key={message}>{message}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <Button
              variant="primary"
              disabled={validationMessages.length > 0}
              icon={<Play aria-hidden="true" className="size-4" />}
              onClick={onStart}
            >
              开始检查
            </Button>
          </div>
        </div>
      </div>
    </main>
  );
}

interface SheetMappingEditorProps {
  readonly sheet: ParsedSheetMeta;
  readonly state: SheetMappingState;
  readonly onChange: (state: SheetMappingState) => void;
  readonly onHeaderRowChange: (row: number) => void;
  readonly onFieldChange: (field: FieldKey, column: number | null) => void;
}

function SheetMappingEditor({
  sheet,
  state,
  onChange,
  onHeaderRowChange,
  onFieldChange,
}: SheetMappingEditorProps) {
  const lowConfidence = mappingNeedsAttention(state.mapping);
  const maxHeaderRow = Math.max(1, sheet.actualRowCount);
  const [headerRowDraft, setHeaderRowDraft] = useState({
    source: state.mapping.headerRow,
    value: String(state.mapping.headerRow),
  });
  const displayedHeaderRow =
    headerRowDraft.source === state.mapping.headerRow
      ? headerRowDraft.value
      : String(state.mapping.headerRow);
  const headerValues =
    sheet.scanRows[state.mapping.headerRow - 1] ??
    Array.from({ length: sheet.columnCount }, () => '');
  const missing = REQUIRED_FIELD_KEYS.filter((field) => state.mapping.fields[field] === undefined);

  return (
    <section className="overflow-hidden rounded-xl border border-slate-300 bg-white shadow-sm">
      <div className="grid gap-4 border-b border-slate-200 bg-slate-50 px-4 py-4 sm:px-5 lg:grid-cols-[1fr_auto_auto] lg:items-center">
        <div>
          <h2 className="text-lg font-black text-slate-900">{sheet.name}</h2>
          <p className="mt-1 text-xs text-slate-500">
            有效行 {sheet.effectiveRowCount}，数据范围至第 {sheet.actualRowCount} 行
          </p>
        </div>
        <label className="flex items-center gap-3 text-sm font-semibold text-slate-700">
          表头行
          <input
            aria-label={`${sheet.name} 表头行`}
            type="number"
            min={1}
            max={maxHeaderRow}
            value={displayedHeaderRow}
            onChange={(event) => {
              const value = event.target.value;
              setHeaderRowDraft({ source: state.mapping.headerRow, value });
              const next = Number(value);
              if (Number.isInteger(next) && next >= 1 && next <= maxHeaderRow) {
                onHeaderRowChange(next);
              }
            }}
            onBlur={() => {
              const next = Math.max(1, Math.min(maxHeaderRow, Number(displayedHeaderRow) || 1));
              setHeaderRowDraft({
                source: state.mapping.headerRow,
                value: String(next),
              });
              if (next !== state.mapping.headerRow) onHeaderRowChange(next);
            }}
            className="h-10 w-20 rounded-lg border border-slate-300 bg-white px-3 text-right outline-none focus:border-orange-600 focus:ring-2 focus:ring-orange-100"
          />
        </label>
        <div
          className={`rounded-lg border px-3 py-2 text-xs font-bold ${lowConfidence ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-emerald-200 bg-emerald-50 text-emerald-900'}`}
        >
          识别置信度 {Math.round(state.mapping.confidence * 100)}%
          {lowConfidence && <span className="ml-2 font-medium">请重点确认</span>}
        </div>
      </div>

      <div className="grid gap-6 p-4 sm:p-5 xl:grid-cols-[420px_minmax(0,1fr)]">
        <div>
          <div className="mb-3 flex items-center gap-2">
            <SlidersHorizontal aria-hidden="true" className="size-4 text-slate-500" />
            <h3 className="text-sm font-bold text-slate-900">字段映射</h3>
          </div>
          <div className="space-y-3">
            {FIELD_DEFINITIONS.map((definition) => {
              const currentColumn = state.mapping.fields[definition.key];
              const usedColumns = new Set(
                Object.entries(state.mapping.fields)
                  .filter(([field]) => field !== definition.key)
                  .map(([, column]) => column),
              );
              return (
                <label
                  key={definition.key}
                  className="grid grid-cols-[108px_minmax(0,1fr)] items-center gap-3 text-sm"
                >
                  <span className="font-semibold text-slate-700">
                    {definition.label}
                    {definition.required && (
                      <span className="ml-1 text-red-700" aria-label="必填">
                        *
                      </span>
                    )}
                  </span>
                  <select
                    aria-label={`${sheet.name} ${definition.label}`}
                    value={currentColumn ?? ''}
                    onChange={(event) =>
                      onFieldChange(
                        definition.key,
                        event.target.value === '' ? null : Number(event.target.value),
                      )
                    }
                    className="h-10 min-w-0 rounded-lg border border-slate-300 bg-white px-3 text-slate-800 outline-none focus:border-orange-600 focus:ring-2 focus:ring-orange-100"
                  >
                    <option value="">不映射</option>
                    {headerValues.map((value, columnIndex) => {
                      const column = columnIndex + 1;
                      return (
                        <option key={column} value={column} disabled={usedColumns.has(column)}>
                          {columnName(column)} 列{value.trim() ? `：${value}` : '：空表头'}
                        </option>
                      );
                    })}
                  </select>
                </label>
              );
            })}
          </div>

          {missing.length > 0 && (
            <p
              role="alert"
              className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs leading-5 text-red-800"
            >
              缺少必填映射：{missing.map((field) => getFieldDefinition(field).label).join('、')}
            </p>
          )}

          <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-lg border border-slate-300 bg-slate-50 px-3 py-3 text-sm leading-5 text-slate-700">
            <input
              type="checkbox"
              className="mt-0.5 size-4 accent-orange-600"
              checked={state.mapping.confirmed}
              disabled={missing.length > 0}
              onChange={(event) =>
                onChange({
                  ...state,
                  mapping: { ...state.mapping, confirmed: event.target.checked },
                })
              }
            />
            <span>
              <strong className="block text-slate-900">我已确认此工作表的字段映射</strong>
              自动识别结果不会在未确认时直接用于检查。
            </span>
          </label>
        </div>

        <ExcelPreview sheet={sheet} headerRow={state.mapping.headerRow} />
      </div>
    </section>
  );
}

function ExcelPreview({
  sheet,
  headerRow,
}: {
  readonly sheet: ParsedSheetMeta;
  readonly headerRow: number;
}) {
  const firstRow = Math.max(1, headerRow - 2);
  const rows = sheet.scanRows.slice(firstRow - 1, Math.min(sheet.scanRows.length, headerRow + 7));
  const visibleColumnCount = Math.min(sheet.columnCount, 12);
  return (
    <div className="min-w-0">
      <h3 className="mb-3 text-sm font-bold text-slate-900">数据预览</h3>
      <div className="max-h-[520px] overflow-auto rounded-lg border border-slate-300" tabIndex={0}>
        <table className="min-w-full border-collapse text-xs whitespace-nowrap">
          <thead className="sticky top-0 z-[1] bg-slate-800 text-white">
            <tr>
              <th
                scope="col"
                className="sticky left-0 bg-slate-800 px-3 py-2 text-right font-semibold"
              >
                行
              </th>
              {Array.from({ length: visibleColumnCount }, (_, index) => (
                <th
                  key={index}
                  scope="col"
                  className="border-l border-slate-600 px-3 py-2 text-left font-semibold"
                >
                  {columnName(index + 1)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const excelRow = firstRow + index;
              const isHeader = excelRow === headerRow;
              return (
                <tr
                  key={excelRow}
                  className={
                    isHeader
                      ? 'bg-orange-50 font-bold text-orange-950'
                      : 'bg-white text-slate-700 even:bg-slate-50'
                  }
                >
                  <th
                    scope="row"
                    className={`sticky left-0 border-t border-slate-200 px-3 py-2 text-right font-mono ${isHeader ? 'bg-orange-100' : 'bg-slate-100 text-slate-500'}`}
                  >
                    {excelRow}
                  </th>
                  {Array.from({ length: visibleColumnCount }, (_, columnIndex) => (
                    <td
                      key={columnIndex}
                      className="max-w-64 truncate border-t border-l border-slate-200 px-3 py-2"
                      title={row[columnIndex] ?? ''}
                    >
                      {row[columnIndex] ?? ''}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {rows.length === 0 && (
        <p className="mt-2 text-xs leading-5 text-amber-800">
          该行超出内置预览范围；仍可按列字母手动完成字段映射。
        </p>
      )}
      {sheet.columnCount > visibleColumnCount && (
        <p className="mt-2 text-xs text-slate-500">
          预览仅显示前 {visibleColumnCount} 列，字段下拉框仍包含全部可识别列。
        </p>
      )}
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
